import { create } from 'zustand';
import { conditionalDevtools } from '@/lib/storeMiddleware';
import {
  approveContact as approveContactApi,
  blockContact as blockContactApi,
  fetchContacts,
  updateContactNotifications as updateContactNotificationsApi,
  type BlockContactResult,
} from '@/api/contacts';
import { fetchEmail } from '@/api/emails';
import { UNIFIED_ACCOUNT_ID } from '@/constants/mailbox';
import type { Contact, Email } from '@/types/email';
import { getErrorMessage } from '@/lib/error';
import { useAccountStore } from './accountStore';
import { useFolderStore } from './folderStore';
import { removeEmailsFromStore } from './emailStateHelpers';
import { restoreAfterFailedAction } from './emailCommands';
import { useEmailStore } from './emailStore';
import { mailEvents } from '@/events/mailEvents';

export type ContactsFilter = 'all' | 'pending' | 'approved' | 'blocked';

// Pending contacts are scoped to senders active in the last ~30 days. The
// server's inbox/pending-folder filter is NOT a substitute: a pending sender's
// mail lives in the Pending folder by definition, so that filter matches nearly
// every pending contact ever seen (tens of thousands). The time window is what
// keeps the triage list to the senders that actually need a recent decision.
const RECENT_PENDING_LOOKBACK_MS = 1000 * 60 * 60 * 24 * 30; // ~30 days

// Upper bound on the contact universe loaded into memory for compose/reply
// recipient suggestions (client-side substring match). Well above the server's
// default 200-row page so typed-exact correspondents outside the top 200 still
// surface; see loadContacts.
const CONTACT_SUGGESTION_LIMIT = 2000;

// Pending contacts are filtered server-side to only those with messages in inbox/pending folders

function getContactActivityTimestamp(contact: Contact): number | null {
  const candidates = [
    contact.lastActivityAt,
    contact.lastMessageAt,
    contact.updatedAt,
    contact.createdAt,
  ];
  for (const value of candidates) {
    if (!value) {
      continue;
    }
    const timestamp = new Date(value).getTime();
    if (!Number.isNaN(timestamp)) {
      return timestamp;
    }
  }
  return null;
}

function getContactMessageVolume(contact: Contact): number {
  if (typeof contact.totalMessages === 'number' && Number.isFinite(contact.totalMessages)) {
    return Math.max(0, Math.trunc(contact.totalMessages));
  }
  const sent = contact.sentCount ?? 0;
  const received = contact.receivedCount ?? 0;
  return Math.max(0, Math.trunc(sent + received));
}

function hasDirectOutbound(contact: Contact): boolean {
  return (contact.sentCount ?? 0) > 0;
}

function getPendingReviewPriority(contact: Contact): number {
  if (contact.approvedState !== 'pending') {
    return 0;
  }
  const decision = contact.aiReviewRecommendation?.decision;
  if (decision === 'yes') {
    return 2;
  }
  if (decision === 'no') {
    return 1;
  }
  return 0;
}

function sortContactsByPriority(contacts: Contact[]): Contact[] {
  return [...contacts].sort((a, b) => {
    if (a.approvedState === 'pending' && b.approvedState === 'pending') {
      const aReviewPriority = getPendingReviewPriority(a);
      const bReviewPriority = getPendingReviewPriority(b);
      if (aReviewPriority !== bReviewPriority) {
        return bReviewPriority - aReviewPriority;
      }
    }
    const aSent = hasDirectOutbound(a) ? 1 : 0;
    const bSent = hasDirectOutbound(b) ? 1 : 0;
    if (aSent !== bSent) {
      return bSent - aSent;
    }

    const aVolume = getContactMessageVolume(a);
    const bVolume = getContactMessageVolume(b);
    if (aVolume !== bVolume) {
      return bVolume - aVolume;
    }

    const aScore = typeof a.score === 'number' ? a.score : 0;
    const bScore = typeof b.score === 'number' ? b.score : 0;
    if (aScore !== bScore) {
      return bScore - aScore;
    }

    const aTimestamp = getContactActivityTimestamp(a);
    const bTimestamp = getContactActivityTimestamp(b);
    if (aTimestamp === bTimestamp) {
      return (a.email || '').localeCompare(b.email || '');
    }
    if (aTimestamp === null) {
      return 1;
    }
    if (bTimestamp === null) {
      return -1;
    }
    return bTimestamp - aTimestamp;
  });
}

type ReviewRecommendationMap = Record<string, Contact['aiReviewRecommendation'] | null>;

function mergeReviewRecommendations(
  map: ReviewRecommendationMap,
  contacts: Contact[]
): ReviewRecommendationMap {
  let nextMap = map;
  contacts.forEach((contact) => {
    if (!contact.id) {
      return;
    }
    const value = contact.aiReviewRecommendation ?? null;
    if (nextMap[contact.id] === value) {
      return;
    }
    if (nextMap === map) {
      nextMap = { ...map };
    }
    nextMap[contact.id] = value;
  });
  return nextMap;
}

function setReviewRecommendationEntry(
  map: ReviewRecommendationMap,
  contactId: string | null | undefined,
  recommendation: Contact['aiReviewRecommendation'] | null
): ReviewRecommendationMap {
  if (!contactId) {
    return map;
  }
  const value = recommendation ?? null;
  if (map[contactId] === value) {
    return map;
  }
  return {
    ...map,
    [contactId]: value,
  };
}

const makePendingContextKey = (accountId: string | null, folderId: string | null): string => {
  return `${accountId ?? 'none'}:${folderId ?? 'all'}`;
};

const resolveAccountForContact = (
  contactId: string,
  contacts: Contact[],
  explicitAccountId?: string | null
): string | null => {
  if (explicitAccountId) {
    return explicitAccountId;
  }
  const existing = contacts.find((contact) => contact.id === contactId);
  if (existing?.accountId) {
    return existing.accountId;
  }
  const accountState = useAccountStore.getState();
  const selected = accountState.selectedAccountId;
  if (selected && selected !== UNIFIED_ACCOUNT_ID) {
    return selected;
  }
  return accountState.actualAccounts()[0]?.id ?? null;
};

interface ContactStoreState {
  contacts: Contact[];
  contactsLoading: boolean;
  contactsLoaded: boolean;
  contactsError: string | null;
  filter: ContactsFilter;
  search: string;
  activeAccountId: string | null;
  pendingContacts: Contact[];
  pendingContactsLoading: boolean;
  pendingContactsLoaded: boolean;
  pendingContactsError: string | null;
  pendingContextKey: string | null;
  pendingContactsAbortController: AbortController | null;
  pendingContactsRequestKey: string | null;
  pendingContactsRequestPromise: Promise<Contact[]> | null;
  reviewRecommendations: ReviewRecommendationMap;

  setFilter: (filter: ContactsFilter) => void;
  setSearch: (value: string) => void;
  pendingContactsCount: () => number;
  filteredContacts: () => Contact[];
  loadPendingContacts: (options: { accountId: string | null; folderId?: string | null; force?: boolean }) => Promise<Contact[]>;
  setActiveAccount: (accountId: string | null) => Promise<void>;
  loadContacts: (accountId: string) => Promise<Contact[]>;
  ensureUnifiedContacts: () => Promise<void>;
  updateContactReviewRecommendation: (contactId: string, recommendation: Contact['aiReviewRecommendation'] | null) => void;
  approveContact: (
    contactId: string,
    options?: { accountId?: string | null }
  ) => Promise<Contact | null>;
  blockContact: (
    contactId: string,
    /**
     * `initiator` decides whether the block may MOVE THE READER, mirroring
     * removeEmailsFromStore's option of the same name. Defaults to `passive`
     * (S8): a block dispatched from a list row or the bulk bar must leave a
     * reader that may be showing an unrelated message exactly as it was. The
     * detail view passes `user` — there the block acts on the message being
     * read, so it closes/advances like archive or delete.
     */
    options?: { accountId?: string | null; initiator?: 'user' | 'passive' }
  ) => Promise<BlockContactResult | null>;
  setContactNotifications: (
    contactId: string,
    enabled: boolean,
    options?: { accountId?: string | null }
  ) => Promise<Contact | null>;
  /** Drop contacts that no longer exist server-side (sender alias merge). */
  removeContacts: (contactIds: string[]) => void;
  clearContacts: () => void;
}

export const useContactStore = create<ContactStoreState>()(
  conditionalDevtools((set, get) => ({
    contacts: [],
    contactsLoading: false,
    contactsLoaded: false,
    contactsError: null,
    filter: 'pending',
    search: '',
    activeAccountId: null,
    pendingContacts: [],
    pendingContactsLoading: false,
    pendingContactsLoaded: false,
    pendingContactsError: null,
    pendingContextKey: null,
    pendingContactsAbortController: null,
    pendingContactsRequestKey: null,
    pendingContactsRequestPromise: null,
    reviewRecommendations: {},

    setFilter: (filter) => set({ filter }),
    setSearch: (value) => set({ search: value }),

    pendingContactsCount: () => get().pendingContacts.length,

    filteredContacts: () => {
      const { contacts, filter, search } = get();
      const normalizedSearch = search.trim().toLowerCase();
      return contacts.filter((contact) => {
        const matchesFilter =
          filter === 'all' ? true : (contact.approvedState ?? 'pending') === filter;
        if (!matchesFilter) {
          return false;
        }
        if (!normalizedSearch) {
          return true;
        }
        const email = contact.email?.toLowerCase() ?? '';
        const displayName = contact.displayName?.toLowerCase() ?? '';
        return email.includes(normalizedSearch) || displayName.includes(normalizedSearch);
      });
    },

    loadPendingContacts: async ({ accountId, folderId = null, force = false }) => {
      const normalizedAccountId = accountId ?? null;
      const contextKey = makePendingContextKey(normalizedAccountId, folderId);
      const state = get();
      if (!force && state.pendingContextKey === contextKey && state.pendingContactsLoaded) {
        return state.pendingContacts;
      }

      if (
        !force &&
        state.pendingContactsRequestKey === contextKey &&
        state.pendingContactsRequestPromise
      ) {
        return state.pendingContactsRequestPromise;
      }

      const abortExisting = () => {
        const previousController = get().pendingContactsAbortController;
        previousController?.abort();
      };

      if (!normalizedAccountId) {
        abortExisting();
        set({
          pendingContacts: [],
          pendingContactsLoading: false,
          pendingContactsLoaded: true,
          pendingContactsError: null,
          pendingContextKey: contextKey,
          pendingContactsAbortController: null,
          pendingContactsRequestKey: null,
          pendingContactsRequestPromise: null,
        });
        return [];
      }

      if (
        normalizedAccountId === UNIFIED_ACCOUNT_ID &&
        useAccountStore.getState().actualAccounts().length === 0
      ) {
        abortExisting();
        set({
          pendingContacts: [],
          pendingContactsLoading: false,
          pendingContactsLoaded: true,
          pendingContactsError: null,
          pendingContextKey: contextKey,
          pendingContactsAbortController: null,
          pendingContactsRequestKey: null,
          pendingContactsRequestPromise: null,
        });
        return [];
      }

      abortExisting();

      const controller = new AbortController();
      set({
        pendingContactsLoading: true,
        pendingContactsError: null,
        pendingContextKey: contextKey,
        pendingContactsAbortController: controller,
        pendingContactsRequestKey: contextKey,
      });

      const activeSinceIso = new Date(Date.now() - RECENT_PENDING_LOOKBACK_MS).toISOString();

      const requestPromise = fetchContacts(normalizedAccountId, {
        approvedState: 'pending',
        includePending: true,
        pendingActiveSince: activeSinceIso,
        signal: controller.signal,
      })
        .then((pending) => {
          const sorted = sortContactsByPriority(pending);
          set((current) => {
            if (current.pendingContactsAbortController !== controller) {
              return {};
            }
            const nextRecommendations = mergeReviewRecommendations(
              current.reviewRecommendations,
              sorted
            );
            return {
              pendingContacts: sorted,
              pendingContactsLoading: false,
              pendingContactsLoaded: true,
              pendingContactsError: null,
              pendingContactsAbortController: null,
              pendingContactsRequestKey: null,
              pendingContactsRequestPromise: null,
              reviewRecommendations: nextRecommendations,
            };
          });
          return sorted;
        })
        .catch((error) => {
          if (error.name === 'AbortError') {
            return [];
          }
          set((current) => {
            if (current.pendingContactsAbortController !== controller) {
              return {};
            }
            return {
              pendingContactsLoading: false,
              pendingContactsError: getErrorMessage(error) ?? 'Failed to load pending contacts',
              pendingContactsLoaded: false,
              pendingContactsAbortController: null,
              pendingContactsRequestKey: null,
              pendingContactsRequestPromise: null,
            };
          });
          throw error;
        })
        .finally(() => {
          set((current) => {
            if (current.pendingContactsAbortController !== controller) {
              return {};
            }
            return {
              pendingContactsAbortController: null,
              pendingContactsRequestKey: null,
              pendingContactsRequestPromise: null,
            };
          });
        });

      set({ pendingContactsRequestPromise: requestPromise });
      return requestPromise;
    },

    setActiveAccount: async (accountId) => {
      const targetAccountId = accountId;
      const { activeAccountId, contactsLoaded } = get();

      if (!targetAccountId) {
        set({
          activeAccountId: null,
          contacts: [],
          contactsLoaded: false,
          contactsLoading: false,
          contactsError: null,
        });
        return;
      }

      if (targetAccountId === activeAccountId && contactsLoaded) {
        return;
      }

      set({
        activeAccountId: targetAccountId,
        contacts: [],
        contactsLoaded: false,
        contactsError: null,
      });

      await get().loadContacts(targetAccountId);
    },

    loadContacts: async (accountId) => {
      if (!accountId) {
        set({
          contacts: [],
          contactsLoaded: false,
          contactsLoading: false,
          activeAccountId: null,
        });
        return [];
      }

      set({
        contactsLoading: true,
        contactsError: null,
        activeAccountId: accountId,
      });

      const activeSinceIso = new Date(Date.now() - RECENT_PENDING_LOOKBACK_MS).toISOString();
      const fetchAccountContacts = async (targetAccountId: string) => {
        return fetchContacts(targetAccountId, {
          includePending: true,
          // Bound the pending portion to recently-active senders; otherwise the
          // suggestion universe balloons with every pending sender ever seen
          // (their mail permanently lives in the Pending folder).
          pendingActiveSince: activeSinceIso,
          // The compose/reply recipient suggestions do a client-side substring
          // match over this in-memory universe, so the server's default 200-row
          // cap would hide any correspondent outside the top 200. Pull a much
          // larger set so a typed-exact address can always be suggested.
          limit: CONTACT_SUGGESTION_LIMIT,
        });
      };

      try {
        let combined: Contact[] = [];
        if (accountId === UNIFIED_ACCOUNT_ID) {
          const accounts = useAccountStore.getState().actualAccounts();
          if (accounts.length) {
            combined = await fetchAccountContacts(UNIFIED_ACCOUNT_ID);
          }
        } else {
          combined = await fetchAccountContacts(accountId);
        }

        const sorted = sortContactsByPriority(combined);
        // Identity guard (X8): a rapid account switch (A→B) must not let A's
        // slower response win and show the wrong account's contacts. This load
        // stamped activeAccountId=accountId at its start; if a newer switch has
        // moved it on, drop this commit.
        if (get().activeAccountId !== accountId) {
          return sorted;
        }
        set((state) => ({
          contacts: sorted,
          contactsLoading: false,
          contactsLoaded: true,
          activeAccountId: accountId,
          reviewRecommendations: mergeReviewRecommendations(state.reviewRecommendations, sorted),
        }));
        return sorted;
      } catch (error) {
        if (get().activeAccountId !== accountId) {
          throw error;
        }
        set({
          contactsLoading: false,
          contactsError: getErrorMessage(error) ?? 'Failed to load contacts',
        });
        throw error;
      }
    },

    ensureUnifiedContacts: async () => {
      if (get().activeAccountId === UNIFIED_ACCOUNT_ID && get().contactsLoaded) {
        return;
      }
      const accounts = useAccountStore.getState().actualAccounts();
      if (!accounts.length) {
        return;
      }
      await get().setActiveAccount(UNIFIED_ACCOUNT_ID);
    },

    updateContactReviewRecommendation: (contactId, recommendation) => {
      if (!contactId) {
        return;
      }
      set((state) => {
        let contactsChanged = false;
        let pendingChanged = false;

        const updateList = (list: Contact[], marker: 'contacts' | 'pending'): Contact[] =>
          list.map((contact) => {
            if (contact.id !== contactId) {
              return contact;
            }
            if (marker === 'contacts') {
              contactsChanged = true;
            } else {
              pendingChanged = true;
            }
            return {
              ...contact,
              aiReviewRecommendation: recommendation ?? null,
            };
          });

        const nextContacts = updateList(state.contacts, 'contacts');
        const nextPendingRaw = updateList(state.pendingContacts, 'pending');
        const nextPending = pendingChanged
          ? sortContactsByPriority(nextPendingRaw)
          : nextPendingRaw;

        const nextRecommendations = setReviewRecommendationEntry(
          state.reviewRecommendations,
          contactId,
          recommendation ?? null
        );

        if (!contactsChanged && !pendingChanged && nextRecommendations === state.reviewRecommendations) {
          return {};
        }

        return {
          contacts: contactsChanged ? nextContacts : state.contacts,
          pendingContacts: pendingChanged ? nextPending : state.pendingContacts,
          reviewRecommendations: nextRecommendations,
        };
      });
    },

    approveContact: async (contactId, options) => {
      const accountId = resolveAccountForContact(contactId, get().contacts, options?.accountId);
      if (!accountId) {
        console.warn('No account available for approving contact');
        return null;
      }

      // Optimistic update: immediately mark contact as approved
      const previousContacts = get().contacts;
      const previousPendingContacts = get().pendingContacts;
      set((state) => ({
        contacts: state.contacts.map((contact) =>
          contact.id === contactId
            ? { ...contact, approvedState: 'approved' as const }
            : contact
        ),
        pendingContacts: state.pendingContacts.filter((contact) => contact.id !== contactId),
      }));
      mailEvents.emit('mail:contact:stateChanged', {
        contactId,
        approvedState: 'approved',
        notificationsEnabled: true,
      });

      // Optimistically remove emails from the current view (they'll be moved
      // to inbox by the server). Snapshot first — the failure path restores
      // them (N12: they previously stayed removed with a console-only error).
      const removedEmailEntries = useEmailStore.getState().emails
        .filter((email) => email.contactId === contactId)
        .map((email) => ({ id: email.id, email }));
      if (removedEmailEntries.length) {
        // S8: this sweep can include the OPEN email (approve from the reader
        // banner) — it must not auto-advance the reader onto a neighbor the
        // user never chose. The grace stamp (markRemoved) stays: the rows are
        // moving server-side and should hide in the source folder meanwhile.
        removeEmailsFromStore(removedEmailEntries.map((entry) => entry.id), {
          initiator: 'passive',
        });
      }

      try {
        const updated = await approveContactApi(accountId, contactId);
        // Reconcile with server data
        set((state) => ({
          contacts: state.contacts.map((contact) =>
            contact.id === updated.id ? updated : contact
          ),
          reviewRecommendations: setReviewRecommendationEntry(
            state.reviewRecommendations,
            updated.id,
            updated.aiReviewRecommendation ?? null
          ),
        }));
        // The server moved emails from pending/blocked to inbox.
        // Fetch only those specific emails and merge them into the store
        // so they appear instantly without a full reload.
        if (updated.movedEmailIds?.length) {
          void Promise.all(
            updated.movedEmailIds.map((id: string) =>
              fetchEmail(accountId, id).catch(() => null)
            )
          ).then((results) => {
            const emails = results.filter((e): e is NonNullable<typeof e> => e !== null);
            if (emails.length) {
              useEmailStore.getState().mergeEmails(emails);
            }
          });
        }
        void useFolderStore
          .getState()
          .loadFolders(accountId)
          .catch((error) => {
            console.error('Failed to refresh folders after approving contact', error);
          });
        return updated;
      } catch (error) {
        console.error('Failed to approve contact', error);
        // Revert the optimistic contact state and restore the removed emails
        // (N12) — callers own the user-facing toast.
        set({ contacts: previousContacts, pendingContacts: previousPendingContacts });
        mailEvents.emit('mail:contact:stateChanged', {
          contactId,
          approvedState: previousContacts.find((c) => c.id === contactId)?.approvedState ?? 'pending',
        });
        if (removedEmailEntries.length) {
          restoreAfterFailedAction(removedEmailEntries);
        }
        throw error;
      }
    },

    blockContact: async (contactId, options) => {
      const accountId = resolveAccountForContact(contactId, get().contacts, options?.accountId);
      if (!accountId) {
        console.warn('No account available for blocking contact');
        return null;
      }

      // Optimistic: update contact state and remove matching emails immediately
      const previousContacts = get().contacts;
      const previousPendingContacts = get().pendingContacts;
      set((state) => ({
        contacts: state.contacts.map((contact) =>
          contact.id === contactId
            ? { ...contact, approvedState: 'blocked' as const }
            : contact
        ),
        pendingContacts: state.pendingContacts.filter((contact) => contact.id !== contactId),
      }));
      mailEvents.emit('mail:contact:stateChanged', {
        contactId,
        approvedState: 'blocked',
      });

      // Optimistically remove emails from this contact. Snapshot first — the
      // failure path restores them (N12).
      const emailState = useEmailStore.getState();
      const removedEmailEntries = emailState.emails
        .filter((email) => email.contactId === contactId)
        .map((email) => ({ id: email.id, email }));
      // S8 is the DEFAULT, not the rule: a block dispatched from a list row or
      // the bulk bar must not auto-advance a reader that may be showing an
      // unrelated message. A block dispatched from the open message is the
      // opposite case — the user acted on what they are reading, so it closes /
      // advances exactly like archive or delete. Only then do the reader's own
      // messages join the removal set: the open message and the blocked sender's
      // thread members are not necessarily list rows, and the open thread has to
      // collapse around them for the advance to land somewhere real.
      const initiator = options?.initiator ?? 'passive';
      if (initiator === 'user') {
        const alreadyRemoving = new Set(removedEmailEntries.map((entry) => entry.id));
        const addReaderEntry = (email: Email | null | undefined) => {
          if (!email || email.contactId !== contactId || alreadyRemoving.has(email.id)) {
            return;
          }
          alreadyRemoving.add(email.id);
          removedEmailEntries.push({ id: email.id, email });
        };
        addReaderEntry(emailState.selectedEmail);
        emailState.selectedThread?.messages.forEach(addReaderEntry);
      }
      const emailsToRemove = removedEmailEntries.map((entry) => entry.id);
      if (emailsToRemove.length) {
        removeEmailsFromStore(emailsToRemove, { initiator });
      }

      try {
        const result = await blockContactApi(accountId, contactId);
        // Reconcile with server data
        set((state) => ({
          contacts: state.contacts.map((contact) =>
            contact.id === result.contact.id ? result.contact : contact
          ),
          reviewRecommendations: setReviewRecommendationEntry(
            state.reviewRecommendations,
            result.contact.id,
            result.contact.aiReviewRecommendation ?? null
          ),
        }));
        // Remove any additional emails the server moved that we didn't catch optimistically
        const alreadyRemoved = new Set(emailsToRemove);
        const additionalIds = result.movedEmailIds.filter((id: string) => !alreadyRemoved.has(id));
        if (additionalIds.length) {
          // S8: ALWAYS passive, even when wave 1 was user-initiated. This wave
          // lands seconds after the click and can sweep rows whose contactId
          // wasn't hydrated at wave 1 — including the message the reader just
          // advanced to. A second advance here is the flash-forward.
          removeEmailsFromStore(additionalIds, { initiator: 'passive' });
        }
        void useFolderStore
          .getState()
          .loadFolders(accountId)
          .catch((error) => {
            console.error('Failed to refresh folders after blocking contact', error);
          });
        return result;
      } catch (error) {
        console.error('Failed to block contact', error);
        // Revert optimistic contact state on failure
        set({ contacts: previousContacts, pendingContacts: previousPendingContacts });
        mailEvents.emit('mail:contact:stateChanged', {
          contactId,
          approvedState: previousContacts.find((c) => c.id === contactId)?.approvedState ?? 'approved',
        });
        // Restore the optimistically-removed emails too (N12) — callers own
        // the user-facing toast.
        if (removedEmailEntries.length) {
          restoreAfterFailedAction(removedEmailEntries);
        }
        throw error;
      }
    },

    setContactNotifications: async (contactId, enabled, options) => {
      const accountId = resolveAccountForContact(contactId, get().contacts, options?.accountId);
      if (!accountId) {
        console.warn('No account available for updating contact notifications');
        return null;
      }
      try {
        const updated = await updateContactNotificationsApi(accountId, contactId, enabled);
        set((state) => ({
          contacts: state.contacts.map((contact) =>
            contact.id === updated.id ? updated : contact
          ),
          reviewRecommendations: setReviewRecommendationEntry(
            state.reviewRecommendations,
            updated.id,
            updated.aiReviewRecommendation ?? null
          ),
        }));
        mailEvents.emit('mail:contact:stateChanged', {
          contactId: updated.id,
          approvedState: updated.approvedState,
          notificationsEnabled: updated.notificationsEnabled ?? true,
        });
        return updated;
      } catch (error) {
        console.error('Failed to update contact notifications', error);
        throw error;
      }
    },

    removeContacts: (contactIds) => {
      if (!contactIds.length) {
        return;
      }
      const removed = new Set(contactIds);
      set((state) => ({
        contacts: state.contacts.filter((contact) => !removed.has(contact.id)),
        pendingContacts: state.pendingContacts.filter((contact) => !removed.has(contact.id)),
      }));
    },

    clearContacts: () => {
      const controller = get().pendingContactsAbortController;
      controller?.abort();
      set({
        contacts: [],
        contactsLoaded: false,
        contactsLoading: false,
        contactsError: null,
        activeAccountId: null,
        pendingContacts: [],
        pendingContactsLoading: false,
        pendingContactsLoaded: false,
        pendingContactsError: null,
        pendingContextKey: null,
        pendingContactsAbortController: null,
        pendingContactsRequestKey: null,
        pendingContactsRequestPromise: null,
        reviewRecommendations: {},
      });
    },
  }))
);
